import { useCallback, useEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import {
  CreditCardBrand,
  InvoiceFilterType,
  InvoiceQuickFilterOptions,
  QuickFilterOptions,
} from '@frontend/api-invoices';
import { PaymentQueries } from '@frontend/api-payments';
import { RefundFilterType } from '@frontend/api-refunds';
import { Header } from '@frontend/header-navigation';
import {
  BulkCollectionsDescriptionTextMap,
  BULK_COLLECTIONS_ARTICLE_LINK,
  BulkCollectionsBannerType,
  BulkCollectionsPromoTrackingIds,
  PaymentsPromoBanner,
  PromoBannerBadge,
  useBulkCollectionsBannerEligibility,
} from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { CollectionsPageImage } from '@frontend/payments-assets';
import { usePaymentRole } from '@frontend/payments-hooks';
import { useQueryPaginatedInvoices, useInvoiceShallowStore } from '@frontend/payments-invoice-controller';
import { useBulkCollectionsUIShallowStore } from '@frontend/payments-state';
import { useChargesEnabledLocations } from '@frontend/person-invoice-create';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { pendo } from '@frontend/tracking';
import { useNavigate, useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { BannerNotification } from '@frontend/design-system';
import { CollectionList } from '../../components/Collections/CollectionsList/collection-list';
import { InvoiceList } from '../../components/Invoices/InvoiceList';
import { NewInvoiceAction } from '../../components/Invoices/NewInvoiceAction';
import { InvoiceSummaryCard } from '../../components/Invoices/SummaryCard/invoice-summary-card';
import { DateFilters } from '../../components/payment-requests/filters/date-filter';
import { StatusTabs } from '../../components/payment-requests/filters/status-tabs';
import { PrintRequestListTable } from '../../components/payment-requests/table/list-table';
import { FailedList, PrintFailedList } from '../../components/PaymentFailed/FailedList';
import { PaymentsPage } from '../../components/PaymentsPage';
import { PrintRefundList, RefundList } from '../../components/Refunds/RefundList';
import { RefundSummaryCard } from '../../components/Refunds/RefundSummary';
import { SearchFailedPaymentsParams } from '../../hooks/payment-failed/types';
import { usePaymentRequestQuickFilter } from '../../hooks/payment-requests/use-payment-request-quick-filters';
import { useRefundShallowStore } from '../../hooks/refund-provider';
import { useFailedTransactionsFilters } from '../../hooks/use-failed-transactions-filters';
import { usePrintDialogue } from '../../hooks/use-print-dialogue';
import { useRefundSearch } from '../../hooks/use-refund-search';
import {
  mapFailedPaymentsOriginToPaymentsOrigin,
  mapPaymentOriginToFailedPaymentsOrigin,
} from '../../hooks/utils/store-origin-maps';
import { usePaymentRequestsPanelShallowStore } from '../../store/payment-requests-panel-store';
import { COLLECTIONS_PORTAL_TARGET_ID } from '../../types/payment-request';

const styles = {
  responsiveHeading: css`
    @media (max-width: ${breakpoints.xsmall.max}px) {
      padding: ${theme.spacing(2)};
    }
  `,
  container: css`
    margin-bottom: ${theme.spacing(3)};
    @media (max-width: ${breakpoints.small.max}px) {
      margin-bottom: ${theme.spacing(0)};
      background-color: ${theme.colors.neutral[5]};
      border-top: 1px solid ${theme.colors.neutral[20]};
      border-bottom: 1px solid ${theme.colors.neutral[20]};
      padding: ${theme.spacing(2, 3)};
      gap: ${theme.spacing(1)};
    }
  `,
};

const onChangeStatusEmpty = () => {};

type StatusView = 'collections' | 'invoices' | 'refunds' | 'failed';

const INVOICE_STATUSES = ['default', 'allInvoices', 'paid', 'unpaid'];

const isInvoiceStatus = (status: QuickFilterOptions): status is InvoiceQuickFilterOptions =>
  INVOICE_STATUSES.includes(status);

export const PaymentRequests = () => {
  const { t } = useTranslation('payments');
  const { selectedLocationIds } = useAppScopeStore();
  const { hasAnalyticsRole } = usePaymentRole(selectedLocationIds, 'all');
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  const { disabledMessageWithLink } = useChargesEnabledLocations();

  const hasInitiallyMountedRef = useRef(false);

  const search = useSearch({ from: '/payments/invoices', shouldThrow: false });
  const navigate = useNavigate();

  const { showPrint, setShowPrint, handlePrint } = usePrintDialogue();

  const { selectedStatus, setSelectedStatus } = usePaymentRequestsPanelShallowStore(
    'selectedStatus',
    'setSelectedStatus'
  );

  const { setShouldShowSelectAllCallout } = useBulkCollectionsUIShallowStore('setShouldShowSelectAllCallout');

  const {
    pmsName,
    showBanner,
    bannerType,
    enableCollectionFlow,
    markDismissed,
    dismissPermanently,
    primaryAction,
    isButtonLoading,
  } = useBulkCollectionsBannerEligibility({
    navigateToCollections: () => {
      setShouldShowSelectAllCallout(true);
      setSelectedStatus('collections');
    },
  });

  const { invalidateUnrecordedCount } = PaymentQueries.usePaymentsInvalidation();

  const {
    filter: invoiceFilter,
    setFilter: setInvoicesFilter,
    setFilterDisplayDates,
    setSelectedStatus: setSelectedInvoiceViewStatus,
  } = useInvoiceShallowStore('filter', 'setFilter', 'setFilterDisplayDates', 'setSelectedStatus');

  const { filter: refundFilter, setFilter: setRefundFilter } = useRefundShallowStore('filter', 'setFilter');

  const {
    filter: failedFilter,
    filterMethods: failedFilterMethods,
    currentPage: failedCurrentPage,
  } = useFailedTransactionsFilters();

  const setSelectedViewStatus = (status: QuickFilterOptions) => {
    setSelectedStatus(status);
    if (isInvoiceStatus(status)) {
      setSelectedInvoiceViewStatus(status);
    }
  };

  const onQuickFilterChange = (filter: InvoiceFilterType) => {
    setInvoicesFilter(filter);
  };

  const { handleQuickFilter } = usePaymentRequestQuickFilter({
    filter: invoiceFilter,
    onChange: onQuickFilterChange,
  });

  const onChangeStatus = useCallback(
    (selected: QuickFilterOptions) => {
      if (selectedStatus === selected) {
        return;
      }
      setSelectedViewStatus(selected);
      if (isInvoiceStatus(selected)) {
        handleQuickFilter(selected);
      }
    },
    [invoiceFilter, selectedStatus]
  );

  const onChangeDateRange = (dateRange: InvoiceFilterType['dateRange']) => {
    setFilterDisplayDates(dateRange);
  };

  const statusView: StatusView = useMemo(() => {
    if (isInvoiceStatus(selectedStatus)) {
      return 'invoices';
    }

    if (selectedStatus === 'refunded') {
      return 'refunds';
    }

    if (selectedStatus === 'failed') {
      return 'failed';
    }

    if (selectedStatus === 'collections') {
      return 'collections';
    }

    console.error('Unknown selected status: ', selectedStatus);
    return 'invoices';
  }, [selectedStatus]);

  /**
   * When refund store changes are made, map them, as best we can, to the invoice store, to
   * preserve continuity
   */
  const onChangeRefundFilter = useCallback(
    (newRefundFilter: RefundFilterType) => {
      const cardType = newRefundFilter.cardType?.map((c) =>
        c === '' ? CreditCardBrand.CardBrandUnknown : (c.toUpperCase() as CreditCardBrand)
      );

      failedFilterMethods.setFilter({
        ...failedFilter,
        search: {
          ...failedFilter.search,
          origin: mapPaymentOriginToFailedPaymentsOrigin(newRefundFilter.origin || []),
          personName: newRefundFilter.personName,
        },
      });

      setInvoicesFilter({
        ...invoiceFilter,
        person: newRefundFilter.personName,
        paymentType: newRefundFilter.origin,
        paymentMethod: newRefundFilter.paymentType,
        cardType,
      });
    },
    [invoiceFilter, failedFilter]
  );

  const onChangeInvoiceFilter = useCallback(
    (newInvoiceFilter: InvoiceFilterType) => {
      const cardType = newInvoiceFilter.cardType?.map((c) =>
        c === CreditCardBrand.CardBrandUnknown ? '' : (c.toLowerCase() as CreditCardBrand)
      );

      setRefundFilter({
        ...refundFilter,
        personName: newInvoiceFilter.person,
        origin: newInvoiceFilter.paymentType,
        paymentType: newInvoiceFilter.paymentMethod,
        cardType,
      });

      failedFilterMethods.setFilter({
        ...failedFilter,
        search: {
          ...failedFilter.search,
          origin: mapPaymentOriginToFailedPaymentsOrigin(newInvoiceFilter.paymentType || []),
          personName: newInvoiceFilter.person,
        },
      });
    },
    [refundFilter]
  );

  const onChangeFailedFilter = useCallback(
    (toSet: SearchFailedPaymentsParams) => {
      const mappedOrigin = mapFailedPaymentsOriginToPaymentsOrigin(toSet.search.origin || []);
      setInvoicesFilter({
        ...invoiceFilter,
        person: toSet.search.personName,
        paymentType: mappedOrigin,
      });

      setRefundFilter({
        ...refundFilter,
        personName: toSet.search.personName,
        origin: mappedOrigin,
      });
    },
    [invoiceFilter, refundFilter]
  );

  useEffect(() => {
    /**
     * Main date change state in invoice store.
     * Watch it, and sync refund store.
     *
     * `onChangeDateRange()` doesn't have the latest store states,
     * even though it's not memoized. So, get around messing up (clearing) state by using useEffect().
     */

    const { dateRange } = invoiceFilter;

    setRefundFilter({ ...refundFilter, dateRefunded: { lte: dateRange?.end, gte: dateRange?.start } });
    failedFilterMethods.setSubmittedAt(dateRange?.start, dateRange?.end);
  }, [invoiceFilter.dateRange]);

  useEffect(() => {
    /**
     * Tracking only. Not changing any functionality.
     */
    pendo.track(`payments--requests-page--view`, {
      activeTab: selectedStatus,
    });
  }, [selectedStatus]);
  useEffect(() => {
    if (hasInitiallyMountedRef.current) {
      return;
    }

    let statusFromUrl = search?.status as QuickFilterOptions | undefined;
    let statusFromStore = selectedStatus;

    // If collections are disabled but the store still has 'collections',
    // fall back to the default invoices view.
    if (!enableCollectionFlow && statusFromStore === 'collections') {
      statusFromStore = 'allInvoices';
    }
    if (!enableCollectionFlow && statusFromUrl === 'collections') {
      statusFromUrl = 'allInvoices';
    }
    hasInitiallyMountedRef.current = true;

    if (statusFromUrl) {
      /**
       * On initial mount, if the URL is set, prioritize that.
       */
      if (['failed', 'allInvoices', 'paid', 'refunded', 'unpaid', 'collections'].includes(statusFromUrl)) {
        setSelectedViewStatus(statusFromUrl as QuickFilterOptions);
      }

      navigate({ to: '/payments/invoices', search: { ...search, status: undefined }, replace: true });
    } else if (statusFromStore) {
      /**
       * When revisiting a page, the store status may already be customized.
       * To get here, the URL param is not set, so set it.
       */

      setSelectedViewStatus(statusFromStore as QuickFilterOptions);
    }
  }, [search?.status, enableCollectionFlow, selectedStatus]);

  useEffect(() => {
    /**
     * On mount
     */
    // Revalidate unrecorded on mount
    invalidateUnrecordedCount();

    // sync refund date range
    setRefundFilter({
      ...refundFilter,
      dateRefunded: { lte: invoiceFilter?.dateRange?.end, gte: invoiceFilter?.dateRange?.start },
    });
  }, []);

  return (
    <PaymentsPage
      title={
        <div>
          <Header
            title={isMobile ? t('invoiceHeaderTitleCondensed') : t('invoiceHeaderTitle')}
            css={styles.responsiveHeading}
          />
          <div id={COLLECTIONS_PORTAL_TARGET_ID} />
        </div>
      }
      action={
        <div css={styles.responsiveHeading}>
          <NewInvoiceAction trackingId='pay-portal-invoices-button-new' locationIds={selectedLocationIds} />
        </div>
      }
      css={css`
        @media (max-width: ${breakpoints.xsmall.max}px) {
          padding: ${theme.spacing(0)};
        }
      `}
      showPrint={showPrint}
      setShowPrint={setShowPrint}
      printDialogue={({ onClose }) => (
        <>
          {statusView === 'invoices' && (
            <PrintRequestListTable
              headerContent={
                <>
                  <StatusTabs selectedStatus={selectedStatus} onChange={onChangeStatusEmpty} />
                  <DateFilters dateRange={invoiceFilter.dateRange} />
                  {hasAnalyticsRole && <InvoiceSummaryPerformanceWrapper loading={false} />}
                </>
              }
              onClose={onClose}
            />
          )}
          {statusView === 'refunds' && (
            <>
              <PrintRefundList
                onClose={onClose}
                headerContent={
                  <>
                    <StatusTabs selectedStatus={selectedStatus} onChange={onChangeStatusEmpty} />
                    <DateFilters dateRange={invoiceFilter.dateRange} />
                    {hasAnalyticsRole && <RefundSummaryCardPerformanceWrapper />}
                  </>
                }
              />
            </>
          )}
          {statusView === 'failed' && (
            <PrintFailedList
              onClose={onClose}
              filter={failedFilter}
              currentPage={failedCurrentPage}
              headerContent={
                <>
                  <StatusTabs selectedStatus={selectedStatus} onChange={onChangeStatusEmpty} />
                  <DateFilters dateRange={invoiceFilter.dateRange} />
                </>
              }
            />
          )}
        </>
      )}
    >
      <div css={{ position: 'relative' }}>
        {disabledMessageWithLink && (
          <BannerNotification
            css={css`
              margin-bottom: ${theme.spacing(2)};
            `}
            status='error'
          >
            {disabledMessageWithLink}
          </BannerNotification>
        )}
        {!isMobile && showBanner && bannerType === BulkCollectionsBannerType.CollectionsFeatureDisabled && (
          <PaymentsPromoBanner
            headerText={t('Collect outstanding balances from {{pmsName}} all at once.', { pmsName })}
            HeaderPrefix={() => <PromoBannerBadge badgeType='new' />}
            titleText={t('Bulk Collections')}
            descriptionText={
              BulkCollectionsDescriptionTextMap(pmsName)[BulkCollectionsBannerType.CollectionsFeatureDisabled]
            }
            FeatureImage={CollectionsPageImage}
            showLoader={isButtonLoading}
            styleOnContainer={css`
              margin-bottom: var(--fabric-space-padding-md);
            `}
            footerActions={{
              primary: { ...primaryAction, trackingId: `${primaryAction.trackingId}:payments-page` },
              secondary: {
                text: t('Learn More'),
                trackingId: `${BulkCollectionsPromoTrackingIds.learnMore}:${bannerType}:payments-page`,
                onClick: () => {
                  window.open(BULK_COLLECTIONS_ARTICLE_LINK, '_blank', 'noopener,noreferrer');
                },
              },
              tertiary: {
                text: t('Maybe Later'),
                trackingId: `${BulkCollectionsPromoTrackingIds.maybeLater}:${bannerType}:payments-page`,
                onClick: markDismissed,
              },
            }}
          />
        )}
        <StatusTabs
          selectedStatus={selectedStatus}
          onChange={onChangeStatus}
          showCollectionTab={enableCollectionFlow}
          showCollectionTabCallout={showBanner && bannerType === BulkCollectionsBannerType.CollectionsFeatureEnabled}
          onDismissBanner={dismissPermanently}
        />
        {statusView !== 'collections' && (
          <DateFilters dateRange={invoiceFilter.dateRange} onChangeDateRange={onChangeDateRange} />
        )}

        {statusView === 'invoices' && (
          <>
            {hasAnalyticsRole && <InvoiceSummaryPerformanceWrapper />}
            <InvoiceList handlePrint={handlePrint} onChangeFilter={onChangeInvoiceFilter} />
          </>
        )}
        {statusView === 'refunds' && (
          <>
            {hasAnalyticsRole && <RefundSummaryCardPerformanceWrapper />}
            <RefundList handlePrint={handlePrint} onChangeFilter={onChangeRefundFilter} />
          </>
        )}
        {statusView === 'failed' && (
          <FailedList
            handlePrint={handlePrint}
            filter={failedFilter}
            filterMethods={failedFilterMethods}
            onChangeFilter={onChangeFailedFilter}
            currentPage={failedCurrentPage}
          />
        )}

        {enableCollectionFlow && statusView === 'collections' && (
          <CollectionList onDismissBanner={dismissPermanently} />
        )}
      </div>
    </PaymentsPage>
  );
};

const RefundSummaryCardPerformanceWrapper = () => {
  const { summary } = useRefundSearch();

  return <RefundSummaryCard loading={false} summary={summary} css={{ marginBottom: 24 }} />;
};

const InvoiceSummaryPerformanceWrapper = ({ loading: loadingOverride }: { loading?: boolean }) => {
  const { summary, loading, locationIds } = useQueryPaginatedInvoices();

  return (
    <InvoiceSummaryCard
      summary={summary}
      locationIds={locationIds}
      loading={loadingOverride ?? loading}
      style={styles.container}
    />
  );
};
